import { gql } from '../__generated__'

export const NOTIFICATIONS_SUBSCRIPTION = gql(`
  subscription Notifications {
    notifications {
      ...NotificationFragment
    }
  }
`)

export const PROJECT_MILESTONES_SUBSCRIPTION = gql(`
  subscription ProjectMilestones($projectId: UUID!) {
    projectMilestones(projectId: $projectId) {
      __typename
      ... on MilestoneEvent {
        milestone {
          ...ProjectMilestoneBaseFragment
        }
        type
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const DYNAMIC_REMINDERS_SUBSCRIPTION = gql(`
  subscription DynamicReminders {
    dynamicReminders {
      __typename
      ... on DynamicPlanningEvent {
        type
        dynamicPlanning {
          ...DynamicPlanningMilestoneFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const MY_TASKS_SUBSCRIPTION = gql(`
  subscription MyTasksSubscription {
    myTasks {
      __typename
      ... on TaskEvent {
        type
        task {
          ...MyTaskFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const PROJECT_MILESTONE_TASKS_SUBSCRIPTION = gql(`
  subscription MilestoneTasks($milestoneId: UUID!) {
    milestoneTasks(milestoneId: $milestoneId) {
      __typename
      ... on TaskEvent {
        type
        deletedStandbyTaskId
        task {
          ...TaskListFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const PROJECT_MILESTONE_TASK_RELATED_SUBSCRIPTION = gql(`
  subscription TaskTree($rootTaskId: UUID!) {
    taskTree(rootTaskId: $rootTaskId) {
      __typename
      ... on TaskEvent {
        type
        taskTree {
          ...TaskCardFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const ACCOUNTS_SUBSCRIPTION = gql(`
  subscription Accounts {
    accounts {
      type
      account {
        ...AccountFullFragment
      }
    }
  }
`)

export const COMMENTS_SUBSCRIPTION = gql(`
  subscription Comments($entityId: UUID!, $type: CommentEntityType!) {
    comments(entityId: $entityId, type: $type) {
      __typename
      ... on CommentEvent {
        type
        comment {
          ...CommentFragment
          thread {
            id
            title
            alias
          }
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const PROJECTS_SUBSCRIPTION = gql(`
  subscription Projects {
    projects {
      __typename
      ... on ProjectEvent {
        type
        project {
          ...ProjectFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const PROJECT_DETAILS_SUBSCRIPTION = gql(`
  subscription Project($id: UUID!) {
    project(projectId: $id) {
      __typename
      ... on ProjectEvent {
        type
        project {
          ...ProjectFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const GOAL_DETAILS_SUBSCRIPTION = gql(`
  subscription Goal($goalId: UUID!, $includeGoalTree: Boolean = false, $includeTasks: Boolean = false, $includeProjects: Boolean = false, $includeFiles: Boolean = false, $includeResourcesForGoalTree: Boolean = false, $excludeCanceled: Boolean = true) {
    goal(goalId: $goalId) {
      __typename
      ... on GoalEvent {
        type
        goal {
          ...GoalDetailsFragment
          goalTree @include(if: $includeGoalTree) {
            ...GoalTreeFragment
          }
          tasks(excludeCanceled: $excludeCanceled) @include(if: $includeTasks) {
            ...TaskListFragment
          }
          projects @include(if: $includeProjects) {
            ...GoalDetailsProjectsFragment
          }
          ...GoalResourcesFragment @include(if: $includeFiles)
          }
        }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const BOARDS_SUBSCRIPTION = gql(`
  subscription Boards {
    boards {
      __typename
      ... on BoardEvent {
        type
        board {
          ...BoardInfoFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const BOARD_DETAILS_SUBSCRIPTION = gql(`
  subscription Board($boardId: UUID!) {
    board(boardId: $boardId) {
      __typename
      ... on BoardEvent {
        type
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const COLLECTIONS_SUBSCRIPTION = gql(`
  subscription FashionCollections {
    fashionCollections {
      __typename
      ... on FashionCollectionEvent {
        type
        collection {
          ...FashionCollectionFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const COLLECTION_DETAILS_SUBSCRIPTION = gql(`
  subscription FashionCollection($collectionId: UUID!) {
    fashionCollection(collectionId: $collectionId) {
      __typename
      ... on FashionCollectionEvent {
        type
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const ASSIGNED_GOALS_SUBSCRIPTION = gql(`
  subscription AssignedGoals {
    assignedGoals {
      __typename
      ... on GoalEvent {
        type
        goal {
          ...MyGoalFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const GOAL_TASKS_SUBSCRIPTION = gql(`
  subscription GoalTasks($goalId: UUID!) {
    goalTasks(goalId: $goalId) {
      __typename
      ... on TaskEvent {
        type
        task {
          ...TaskListFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const COUNT_SUBSCRIPTION = gql(`
  subscription Counter($counterType: CounterType!, $entityId: UUID) {
    counter(counterType: $counterType, entityId: $entityId) {
      value
    }
  }
`)

export const USER_TASK_COUNTER_SUBSCRIPTION = gql(`
  subscription UserTaskCounter {
    userTaskCounter {
      assignedToMe
    }
  }
`)

export const MY_TASKS_COUNTERS_SUBSCRIPTION = gql(`
  subscription MyTasksCounters {
    userTaskCounter {
      assignedToMe
      assignedByMe
      following
      total
    }
  }
`)

export const ACTIVITY_LOG_SUBSCRIPTION = gql(`
  subscription ActivityLog($entityId: UUID!) {
    activityLog(entityId: $entityId) {
      ...LogEntryFragment
    }
  }
`)

export const TODOS_COUNTERS_SUBSCRIPTION = gql(`
  subscription ToDosCounters {
    userTodoCounter {
      assignedToMe
      assignedByMe
      following
      total
    }
  }
`)

export const PROJECT_SIZE_MATRIX_SUBSCRIPTION = gql(`
  subscription Subscription($projectId: UUID!) {
    projectSizeMatrix(projectId: $projectId) {
      type
      sizeMatrix {
        ...SizeMatrixFragment
      }
    }
  }
`)

export const SIZE_MATRICES_SUBSCRIPTION = gql(`
  subscription SizeMatrices {
    sizeMatrices {
      type
      sizeMatrix {
        ...SizeMatrixWithProjectFragment
      }
    }
  }
`)

export const RESOURCES_SUBSCRIPTION = gql(`
  subscription Resources($entityId: UUID!, $type: ContextType!) {
    resources(entityId: $entityId, type: $type) {
      __typename
      ... on ResourceEvent {
        type
        resource {
          id
          account {
            id
          }
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const RECURRING_DEFINITION_SUBSCRIPTION = gql(`
  subscription RecurringDefinition($recurringDefinitionId: UUID!) {
    recurringDefinition(recurringDefinitionId: $recurringDefinitionId) {
      type
      definition {
        ...RecurringTaskDefinitionFragment
      }
    }
  }
`)

export const RECURRING_DEFINITION_TASKS_SUBSCRIPTION = gql(`
  subscription RecurringDefinitionTasks($recurringDefinitionId: UUID!) {
    recurringDefinitionTasks(recurringDefinitionId: $recurringDefinitionId) {
      __typename
      ... on TaskEvent {
        type
        task {
          ...TaskFragment
        }
      }
      ... on EntityDeletedEvent {
        ...EntityDeletedEventFragment
      }
    }
  }
`)

export const THREAD_COMMENTS_SUBSCRIPTION = gql(`
  subscription ThreadComments {
    threadComments {
      ...ThreadCommentActivityFragment
    }
  }
`)

export const THREAD_LIKES_SUBSCRIPTION = gql(`
  subscription ThreadLikes {
    threadLikes {
      ...ThreadLikeActivityFragment
    }
  }
`)

export const THREAD_NOTIFICATION_SUBSCRIPTION = gql(`
  subscription ThreadNotification {
    threadNotification {
      ...EmptyResponseFragment
    }
  }
`)

export const COMMENTS_READ_SUBSCRIPTION = gql(`
  subscription CommentsRead($entityId: UUID!) {
    commentsRead(entityId: $entityId) {
      account {
        ...AccountFragment
      }
      readAt {
        seconds
      }
    }
  }
`)

export const NOTIFICATIONS_COUNTER_SUBSCRIPTION = gql(`
  subscription NotificationsCounter {
    notificationsCounter
  }
`)
